import React from 'react'
import PropTypes from 'prop-types'
import { ariaProps, dataProps, onProps } from '@ds/react-utils'
import { useTranslate } from '@ds/comp-private'
import { BrowserStack } from '@ds/icons'
import { TextEllipsis } from '../TextEllipsis'
import { ConditionalTag } from '../../internal/components/ConditionalTag'
import { consoleWarn, warnInvalidThemeProp } from '../../logging'
import { CustomPropTypes } from '../../support'
import { useThemeStyles, useIsInk } from '../../theming'
import type {
  AnchorOrButtonForwardRef,
  AriaAttributes,
  EventListenerProps,
} from '../../types'
import { AnchorTarget, anchorTargets } from '../../variables'
import styles from './styles'

export const linkKinds = ['default', 'subtle'] as const

export type LinkKind = (typeof linkKinds)[number]
export type LinkTarget = AnchorTarget

export interface LinkProps
  extends EventListenerProps<HTMLButtonElement | HTMLAnchorElement>,
    AriaAttributes {
  /**
   * The text to present to assistive devices in order to identify the Link.
   * `accessibilityText` replaces the visible `text` for screen reader users.
   */
  accessibilityText?: string
  /**
   * Accepts custom data attributes.
   */
  'data-.*'?: string
  'data-qa'?: string
  /**
   * Disable the Link component.
   *
   * (only valid when it renders a button, this is ignored if an href is provided)
   */
  disabled?: boolean
  /**
   * The discrete styled link. For standalone Links outside of text copy.
   * Removes the text-decoration underline and increases the tap target.
   */
  discrete?: boolean
  /**
   * A React ref to assign to the HTML node representing the Link element.
   */
  forwardedRef?: AnchorOrButtonForwardRef
  /**
   * If href is provided then it will render an anchor, otherwise a button.
   */
  href?: string
  /**
   * An Olive theme only prop to display lighter text for more contrast on dark backgrounds
   */
  isLightText?: boolean
  /**
   * Whether the Link displays as default or subtle.
   */
  kind?: LinkKind
  /**
   * The relationship of the linked URL of a anchor as space-separated link types.
   *
   * Reference: https://developer.mozilla.org/en-US/docs/Web/HTML/Link_types
   */
  rel?: string
  /**
   * Whether or not to display an icon when link opens in a new window (ie. target = '_blank')
   * Recommended true for optimal accessibility.
   * Only in Ink.
   */
  showIconForNewWindow?: boolean
  /**
   * The HTML link target (if an href is _not_ provided, this is ignored).
   * When target="_blank" use rel="noreferrer" or rel="noopener" to avoid the vulnerability.
   */
  target?: AnchorTarget
  /**
   * The text label for the Link.
   */
  text: React.ReactNode
}

/**
 * Links allow users to navigate to other locations.
 */
export function Link({
  accessibilityText,
  'data-qa': dataQa,
  disabled = false,
  discrete = false,
  forwardedRef,
  href,
  isLightText,
  kind = 'default',
  onClick,
  rel,
  showIconForNewWindow = false,
  target,
  text,
  ...restProps
}: LinkProps) {
  const translate = useTranslate()
  const isInk = useIsInk()

  if (isLightText && isInk) {
    warnInvalidThemeProp('Link', 'isLightText', 'Ink')
  }

  const opensNewWindow = !!href && target === '_blank'
  const isValidTextString = typeof text === 'string'
  const isTextWithTextEllipsis =
    React.isValidElement(text) && text.type === TextEllipsis

  if (!accessibilityText && !isValidTextString) {
    consoleWarn(`
      If you do not pass a string to the 'text' prop, you should pass clarifying
      'accessibilityText' for screen reader users.
    `)
  }

  const sx = useThemeStyles(styles)

  const linkStyles = [
    sx.default.link,
    kind && sx[kind].link,
    discrete && sx.discrete.link,
    isLightText && !isInk && sx.lightText,
    isTextWithTextEllipsis && { display: 'inline-flex' },
  ]
  const iconStyles = [sx.default.icon, discrete && sx.discrete.icon]

  const linkNode = (
    <span
      data-qa={dataQa && `${dataQa}-text`}
      css={{ textDecoration: 'inherit' }}
    >
      {text}

      {opensNewWindow && showIconForNewWindow && isInk && (
        <span css={iconStyles} data-qa={dataQa && `${dataQa}-new-window-icon`}>
          <BrowserStack />
        </span>
      )}
    </span>
  )

  const getA11yLinkText = (): string => {
    if (accessibilityText) {
      return accessibilityText
    }
    // If it gets to this point, we know text is a valid string
    return text as string
  }

  return (
    <ConditionalTag
      aria-label={
        opensNewWindow && isValidTextString
          ? translate('OLIVE:LINK_OPENS_IN_NEW_WINDOW:A11Y', {
              LINK: getA11yLinkText(),
            })
          : accessibilityText
      }
      {...ariaProps(restProps)}
      {...dataProps(restProps)}
      {...onProps(restProps)}
      css={linkStyles}
      data-qa={dataQa}
      disabled={disabled}
      forceElement={discrete ? 'button' : 'span'}
      forwardedRef={forwardedRef}
      href={href}
      onClick={onClick}
      rel={rel}
      target={target}
    >
      {linkNode}
    </ConditionalTag>
  )
}

Link.kinds = linkKinds
Link.targets = anchorTargets

Link.propTypes = {
  accessibilityText: PropTypes.string,
  'data-.*': PropTypes.string,
  disabled: PropTypes.bool,
  discrete: PropTypes.bool,
  forwardedRef: CustomPropTypes.ReactRef,
  href: PropTypes.string,
  isLightText: PropTypes.bool,
  kind: PropTypes.oneOf(linkKinds),
  /**
   * Accepts attributes matching the pattern on[A-Z].* in order to register event handlers.
   */
  'on[A-Z].*': PropTypes.func,
  /**
   * The function to call when a 'click' event is fired.
   */
  onClick: PropTypes.func,
  rel: PropTypes.string,
  showIconForNewWindow: PropTypes.bool,
  target: PropTypes.oneOf(anchorTargets),
  text: PropTypes.node.isRequired,
}

Link.displayName = 'Link'
